import * as React from "react"
import * as SliderPrimitive from "@radix-ui/react-slider"

import { cn } from "lib/utils"

// `safeReleasePointerCapture` swallows the browser's InvalidPointerIdError
// that Radix Slider can throw when the user releases the pointer outside the
// thumb's bounds. See radix-ui/primitives#1804. Functional drag behaviour is
// unaffected; only the spurious console error goes away.
function safeReleasePointerCapture(el) {
  if (!el || el.__patchedReleasePointerCapture) return;
  const original = el.releasePointerCapture.bind(el);
  // eslint-disable-next-line func-names
  el.releasePointerCapture = function (pointerId) {
    try {
      return original(pointerId);
    } catch (_e) {
      // pointer was already released; nothing to do
      return undefined;
    }
  };
  el.__patchedReleasePointerCapture = true;
}

// Renders one `<Thumb />` per value so range sliders (value = [start, end])
// work correctly. Stock shadcn primitive only renders a single thumb, which
// causes Radix to drop pointer events for the second handle of a multi-value
// slider.
const Slider = React.forwardRef(
  ({ className, defaultValue, value, min = 0, max = 100, ...props }, ref) => {
    const _values = React.useMemo(
      () =>
        Array.isArray(value)
          ? value
          : Array.isArray(defaultValue)
            ? defaultValue
            : [min, max],
      [value, defaultValue, min, max]
    )

    return (
      <SliderPrimitive.Root
        ref={ref}
        defaultValue={defaultValue}
        value={value}
        min={min}
        max={max}
        className={cn("relative flex w-full touch-none select-none items-center", className)}
        {...props}>
        <SliderPrimitive.Track
          className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-primary/20">
          <SliderPrimitive.Range className="absolute h-full bg-primary" />
        </SliderPrimitive.Track>
        {_values.map((_, i) => (
          <SliderPrimitive.Thumb
            key={i}
            ref={safeReleasePointerCapture}
            className="block h-4 w-4 rounded-full border border-primary/50 bg-background shadow transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50" />
        ))}
      </SliderPrimitive.Root>
    );
  }
)
Slider.displayName = SliderPrimitive.Root.displayName

export { Slider }
